<script setup lang="ts">
import { shallowRef } from 'vue';
import { store } from '@/app/store';
import { useDeveloperStore } from '@/app/stores/developer';
import { restApi } from '@/services/api';
import { sentryService } from '@/services/telemetry/sentry';
// biome-ignore lint/correctness/noUnusedImports: used via tags <UiRepeatedClickCounter> and <UiNumericKeyboard> in <template>
import { UiNumericKeyboard, UiRepeatedClickCounter } from '@/ui-kit';
// biome-ignore lint/correctness/noUnusedImports: used via tag <KioskDiagnosticsPanel> in <template>
import KioskDiagnosticsPanel from './KioskDiagnosticsPanel.vue';

interface TrackPrintLockResponse {
  record_id?: string;
}

const developer = useDeveloperStore();

const isPrintLockKeyboardVisible = shallowRef(false);
const isDiagnosticsPanelVisible = shallowRef(false);

let isPrintLockToggleInFlight = false;
let isWebcamCaptureInFlight = false;

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onPrintLockTap(): void {
  isPrintLockKeyboardVisible.value = true;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function onDiagnosticsTap(): void {
  isDiagnosticsPanelVisible.value = true;
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
async function onPrintLockPinSuccess(): Promise<void> {
  if (isPrintLockToggleInFlight) {
    return;
  }
  isPrintLockToggleInFlight = true;

  try {
    isPrintLockKeyboardVisible.value = false;

    const isNowLocked = !developer.isPrintLockActive;
    developer.isPrintLockActive = isNowLocked;

    // Capture before alert(): its modal loop blocks getUserMedia negotiation.
    const photoBlob = await captureWebcamSnapshot();

    window.alert(isNowLocked ? 'Printing LOCKED' : 'Printing UNLOCKED');

    trackPrintLockToggle(isNowLocked, photoBlob);

    sentryService.addBreadcrumb(
      'print_lock',
      isNowLocked ? 'Printing locked' : 'Printing unlocked',
      { kiosk_slug: store.kiosk?.slug, hasPhoto: !!photoBlob }
    );
  } finally {
    isPrintLockToggleInFlight = false;
  }
}

async function captureWebcamSnapshot(): Promise<Blob | null> {
  if (isWebcamCaptureInFlight) {
    return null;
  }
  isWebcamCaptureInFlight = true;

  let stream: MediaStream | null = null;
  let video: HTMLVideoElement | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    if (!navigator.mediaDevices?.getUserMedia) {
      // biome-ignore lint/suspicious/noConsole: this warning is operator-visible diagnostics
      console.warn(
        '[KioskLayout] mediaDevices API unavailable (non-HTTPS context?)'
      );
      return null;
    }

    stream = await navigator.mediaDevices.getUserMedia({
      video: { width: { ideal: 2560 }, height: { ideal: 1440 } },
    });
    video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    const activeVideo = video;

    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        timeoutId = null;
        reject(new Error('Video metadata timeout (5s)'));
      }, 5000);
      activeVideo.onloadedmetadata = (): void => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        resolve();
      };
      activeVideo.onerror = (event: unknown): void => {
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
        }
        reject(event instanceof Error ? event : new Error(String(event)));
      };
    });
    await activeVideo.play();

    await new Promise((resolve) => setTimeout(resolve, 400));

    canvas = document.createElement('canvas');
    canvas.width = activeVideo.videoWidth || 2560;
    canvas.height = activeVideo.videoHeight || 1440;
    canvas.getContext('2d')?.drawImage(activeVideo, 0, 0);
    const snapshotCanvas = canvas;

    return await new Promise<Blob | null>((resolve) =>
      snapshotCanvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8)
    );
  } catch (_e) {
    return null;
  } finally {
    // Detaching the stream also stops the decoder on long-running kiosks.
    if (video) {
      video.pause();
      video.srcObject = null;
      video.onloadedmetadata = null;
      video.onerror = null;
    }

    // Zeroing releases the GPU-backed pixel buffer without waiting for GC.
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }

    if (stream) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    isWebcamCaptureInFlight = false;
  }
}

async function trackPrintLockToggle(
  isNowLocked: boolean,
  photoBlob: Blob | null
): Promise<void> {
  try {
    if (isNowLocked) {
      const result = (await restApi.trackPrintLock(
        store.kiosk?.slug ?? '',
        photoBlob
      )) as TrackPrintLockResponse | undefined;
      if (result?.record_id) {
        developer.printLockUuid = result.record_id;
      }
    } else {
      const printLockUuid = developer.printLockUuid;
      if (printLockUuid) {
        await restApi.trackPrintUnlock(printLockUuid, photoBlob);
      }
      developer.printLockUuid = null;
    }
  } catch (error) {
    sentryService.captureException(error as Error, {
      tags: { error_type: 'print_lock_tracking_failed' },
      extra: {
        isNowLocked,
        kiosk_slug: store.kiosk?.slug,
        hasPhoto: !!photoBlob,
      },
    });
  }
}
</script>

<template>
  <UiRepeatedClickCounter
    class="KioskLayout__print-lock-trigger"
    data-testid="print-lock-trigger"
    :count="5"
    :showAfterClicks="3"
    @success="onPrintLockTap"
  />

  <UiRepeatedClickCounter
    class="KioskLayout__diagnostics-trigger"
    data-testid="diagnostics-trigger"
    :count="7"
    :showAfterClicks="4"
    @success="onDiagnosticsTap"
  />

  <KioskDiagnosticsPanel
    v-if="isDiagnosticsPanelVisible"
    @close="isDiagnosticsPanelVisible = false"
  />

  <UiNumericKeyboard
    v-if="isPrintLockKeyboardVisible"
    :title="developer.isPrintLockActive ? $t('PrintLock.unlockTitle') : $t('PrintLock.lockTitle')"
    pin="2922"
    :error-message="$t('NumericKeyboard.invalidPin')"
    :clear-label="$t('NumericKeyboard.clear')"
    :enter-label="$t('NumericKeyboard.enter')"
    @success="onPrintLockPinSuccess"
    @close="isPrintLockKeyboardVisible = false"
  />
</template>
